import { xsalsa20poly1305 } from '@noble/ciphers/salsa';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha256';
import { WalletError } from '@cef-ai/wallet-api-client';

/**
 * HKDF info for the root-wrapping key. Deliberately distinct from the signing
 * derivation: `derive.ts` uses the PRF output AS the ed25519 seed, so wrapping
 * under an unlabelled copy of it would encrypt the root under the key it
 * protects. Rotating this label invalidates every stored envelope, so it does
 * not change without a migration.
 */
export const ROOT_WRAP_HKDF_INFO = 'cere-wallet-root-wrap-v1';

/**
 * The `wrapVersion` this client writes into every envelope it creates
 * (`RootEnvelopeWire.wrapVersion`). Bump this, and add an entry to
 * `ROOT_WRAP_LABELS_BY_VERSION`, when a new label is introduced — never
 * repoint an existing version string at a different label.
 */
export const ROOT_WRAP_VERSION = 'v1';

/**
 * Closed map from a `wrapVersion` wire string to its compiled-in HKDF label.
 * This is the ONLY place a `wrapVersion` value is turned into key-derivation
 * input. The map exists so that a server-supplied version string is always
 * looked up against a fixed, compiled-in set rather than used directly as
 * HKDF `info` -- an unrecognised value must be a named, loud error (see
 * `deriveWrapKeyForVersion`), never a silently-derived key under attacker- or
 * server-chosen material.
 *
 * Typed via `as const` (not `Readonly<Record<string, string>>`) so the key
 * set is closed at the TYPE level, not just at runtime: this repo does not
 * enable `noUncheckedIndexedAccess`, so indexing a `Record<string, string>`
 * with an arbitrary `string` types as `string`, not `string | undefined` --
 * the `=== undefined` guard that used to gate this would still work, but the
 * compiler would have no opinion if a later edit deleted it. With `as const`,
 * indexing this object with a plain `string` (as `wrapVersion` off the wire
 * necessarily is) is a compile error, so the only way in is the `in`-narrowed
 * `isKnownWrapVersion` guard below -- deleting *that* guard is what now fails
 * `tsc`, not a silently-widened lookup.
 */
const ROOT_WRAP_LABELS_BY_VERSION = {
  [ROOT_WRAP_VERSION]: ROOT_WRAP_HKDF_INFO,
} as const;

/** The closed set of `wrapVersion` strings this build knows how to unwrap. */
type KnownWrapVersion = keyof typeof ROOT_WRAP_LABELS_BY_VERSION;

/**
 * Type guard narrowing an arbitrary wire `wrapVersion` string down to
 * `KnownWrapVersion`. This is the only place that narrowing happens --
 * `deriveWrapKeyForVersion` cannot index `ROOT_WRAP_LABELS_BY_VERSION` with
 * an unnarrowed `string` without a type error, so this check cannot be
 * silently removed without the compiler noticing.
 */
function isKnownWrapVersion(version: string): version is KnownWrapVersion {
  return Object.prototype.hasOwnProperty.call(ROOT_WRAP_LABELS_BY_VERSION, version);
}

/**
 * Thrown by `deriveWrapKeyForVersion` for a `wrapVersion` outside the
 * compiled-in set.
 *
 * A `WalletError` subclass, not a bare `Error`: the SPA's `walletErrorMessage`
 * only recognises `WalletError`, so anything else reaches the user as raw
 * technical text, and a caller with only a message string to match on cannot
 * tell this apart from the other envelope failures. Subclassing keeps BOTH
 * discriminators — `instanceof UnrecognisedWrapVersionError` and
 * `err.code === 'root-wrap-version-unknown'` — and `wrapVersion` still names
 * the offending value programmatically.
 */
export class UnrecognisedWrapVersionError extends WalletError {
  constructor(public readonly wrapVersion: string) {
    super('root-wrap-version-unknown', `unrecognised root-wrap version: ${JSON.stringify(wrapVersion)}`);
    this.name = 'UnrecognisedWrapVersionError';
  }
}

/**
 * Thrown when an envelope is not wrapped by the credential's PRF output at all
 * — `wrapSource: 'password'`, say, which belongs to the password-recovery path
 * and cannot be opened with a credential key.
 *
 * Its own condition rather than a decrypt failure: attacking a password
 * envelope with a PRF-derived key is fail-safe (it will not open, so no wrong
 * root is ever derived) but it would be diagnosed as "this envelope is
 * damaged" when the truth is "this is the wrong kind of envelope" — a
 * difference between "your data is corrupt" and "use your recovery password".
 */
export class UnsupportedWrapSourceError extends WalletError {
  constructor(public readonly wrapSource: string, public readonly credentialId: string) {
    super(
      'root-wrap-source-unsupported',
      `root envelope for credential ${JSON.stringify(credentialId)} is wrapped by ${JSON.stringify(
        wrapSource,
      )}, not by this credential's PRF output`,
    );
    this.name = 'UnsupportedWrapSourceError';
  }
}

/**
 * Thrown when an envelope's ciphertext will not open under the wrap key derived
 * from a credential's PRF output — a wrong credential, or a corrupt/tampered
 * envelope. Deliberately its own type so the three envelope failures stay three
 * diagnoses:
 *
 *   - `root-envelope-missing` — the server holds no envelope for this
 *     credential (it was never enrolled, or the row is gone);
 *   - `UnrecognisedWrapVersionError` / `root-wrap-version-unknown` — an envelope
 *     exists but names a label this build does not know (a client too old for a
 *     rotated label);
 *   - `UnsupportedWrapSourceError` / `root-wrap-source-unsupported` — the
 *     envelope is not wrapped by a credential PRF at all;
 *   - `RootUnwrapFailedError` / `root-unwrap-failed` — the envelope is the right
 *     kind and a known label, and still will not open.
 *
 * Collapsing them would leave a caller unable to tell "this device was never
 * added to the account" from "upgrade this client" from "use your recovery
 * password" from "this envelope is damaged" — four different next actions. The underlying failure is preserved
 * on `cause`; nothing is swallowed.
 */
export class RootUnwrapFailedError extends WalletError {
  constructor(public readonly credentialId: string, options?: { cause?: unknown }) {
    super('root-unwrap-failed', `root envelope failed to decrypt for credential ${JSON.stringify(credentialId)}`);
    this.name = 'RootUnwrapFailedError';
    // `WalletError`'s constructor takes no `ErrorOptions`, so carry the cause
    // across by hand rather than dropping it — the primitive's own failure is
    // the only detail that says WHY the ciphertext would not open.
    if (options && 'cause' in options) {
      (this as Error).cause = options.cause;
    }
  }
}

/**
 * Derives the root-wrap key for a given `wrapVersion`, resolving it through
 * the compiled-in `ROOT_WRAP_LABELS_BY_VERSION` map. The `wrapVersion` string
 * itself is NEVER passed into the HKDF as `info` -- only the label it maps
 * to is. This is deliberate defense in depth: a leaked or server-supplied
 * `wrapVersion` on its own is not enough to move the derived key, even
 * though the label is not secret and even though an attacker would still
 * need the credential's PRF output to do anything with it. Deciding this
 * once, here, means no future call site has to re-derive the same judgment.
 *
 * Throws `UnrecognisedWrapVersionError` -- distinguishable from a decryption
 * failure -- for any version outside the known set, so "unknown version",
 * "wrong credential", and "corrupt ciphertext" stay three separate,
 * diagnosable conditions instead of collapsing into one generic failure.
 */
export function deriveWrapKeyForVersion(prfOutput: Uint8Array, wrapVersion: string): Uint8Array {
  if (!isKnownWrapVersion(wrapVersion)) {
    throw new UnrecognisedWrapVersionError(wrapVersion);
  }
  const label = ROOT_WRAP_LABELS_BY_VERSION[wrapVersion];
  return deriveWithLabel(prfOutput, label);
}

export function deriveWrapKey(prfOutput: Uint8Array): Uint8Array {
  return deriveWithLabel(prfOutput, ROOT_WRAP_HKDF_INFO);
}

function deriveWithLabel(prfOutput: Uint8Array, label: string): Uint8Array {
  if (prfOutput.length !== 32) {
    // A `WalletError`, not a bare `Error`, for the same reason
    // `UnrecognisedWrapVersionError` is one: `walletErrorMessage` renders a
    // bare `Error`'s own `message` straight into the UI, so an internal
    // sentence about byte lengths would reach the reader as user-facing copy.
    // `validation` is how this file's other caller-fault failures are raised.
    // The length stays in the detail — it is the only thing that says WHAT was
    // wrong, and it is for the log, not for the reader.
    throw new WalletError('validation', `expected 32-byte PRF output, got ${prfOutput.length}`);
  }
  return hkdf(sha256, prfOutput, new Uint8Array(0), label, 32);
}

export interface RootWrapper {
  wrap(root: Uint8Array, key: Uint8Array): { ciphertext: Uint8Array; nonce: Uint8Array };
}
export interface RootUnwrapper {
  unwrap(ciphertext: Uint8Array, nonce: Uint8Array, key: Uint8Array): Uint8Array;
}

/**
 * XSalsa20-Poly1305 (NaCl secretbox shape), matching the envelope format
 * wallet-api stores and the format PR #94's recovery envelope uses — one
 * primitive for device envelopes and recovery envelopes alike.
 *
 * Exposed behind the `RootWrapper`/`RootUnwrapper` interfaces so a consumer
 * can substitute its own wrapper implementation; this one is the provided
 * default, not a mandated one.
 */
export function createSecretboxWrapper(): RootWrapper & RootUnwrapper {
  return {
    wrap(root, key) {
      const nonce = crypto.getRandomValues(new Uint8Array(24));
      // noble returns a subarray view into a larger internal ArrayBuffer
      // (byteOffset 16). Copy it out so callers that reach for `.buffer`
      // (e.g. `Buffer.from(ciphertext.buffer)`) get the right bytes -- this
      // value crosses the injection seam into SPA code we do not control.
      const ciphertext = Uint8Array.from(xsalsa20poly1305(key, nonce).encrypt(root));
      return { ciphertext, nonce };
    },
    unwrap(ciphertext, nonce, key) {
      // Same view-into-larger-buffer issue as encrypt, mirrored on decrypt --
      // and here the view holds the ACCOUNT ROOT in the clear.
      const plaintext = xsalsa20poly1305(key, nonce).decrypt(ciphertext);
      try {
        // `Uint8Array.from` COPIES; the returned root shares no memory with
        // `plaintext`, which is what makes the wipe below safe. (Copying is
        // also what callers reaching for `.buffer` need -- see `wrap`.)
        return Uint8Array.from(plaintext);
      } finally {
        // Wipe the primitive's own output view. It is a second plaintext copy
        // of the root sitting in a buffer this module does not own and cannot
        // otherwise reach again; the caller's copy is the one that lives on.
        // `key` is NOT wiped here: it belongs to the caller, who is the only
        // one who knows whether it is still needed (see
        // `unwrapRootForCredential`, which does wipe it).
        plaintext.fill(0);
      }
    },
  };
}
